import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import type { CompanyRegistration } from "@/graphql/codegen/graphql"
import type { RegistrationFormValues } from "@/screens/CompanySettings/Registration/forms/registrationSchema"
import useRegistrationActions from "@/screens/CompanySettings/Registration/hooks/useRegistrationActions"
import { serializeCreateInput, serializeUpdateInput } from "@/screens/CompanySettings/Registration/utils"

const useRegistrationDialogActions = ({
  onSuccess,
  onClose,
  methods,
}: {
  onSuccess: ((data: CompanyRegistration) => Promise<void>) | undefined
  onClose: () => void
  methods: UseFormReturn<RegistrationFormValues>
}): {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
} => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const companyRegistrationId = methods.watch("companyRegistrationId")

  const isCreateMode = !companyRegistrationId
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { addRegistrationData, updateRegistrationData } = useRegistrationActions({
    onSuccess,
    registrationType: methods.watch("registrationType"),
  })

  const handleSubmit = zodHandleSubmit(async (input: RegistrationFormValues) => {
    if (isCreateMode) {
      await addRegistrationData(serializeCreateInput(input))
    } else {
      await updateRegistrationData(serializeUpdateInput(input))
    }
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  // Ensure the form is valid, dirty, and has no errors (image upload restrictions) before allowing submission
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}

export default useRegistrationDialogActions
